/**
 * @file viem WalletClient factory backed by passkey signing.
 *
 * {@link createPasskeyWalletClient} returns a standard viem `WalletClient`
 * augmented with a `sendCalls` method for batched ERC-4337 intents. All
 * signing operations open the 1auth passkey modal so that private keys never
 * leave the secure enclave on the user's device.
 *
 * The factory is framework-agnostic — it works in any environment that can
 * run viem (React, Vue, plain Node.js scripts, etc.).
 */

import {
  createWalletClient,
  hashTypedData,
  toHex,
  type WalletClient,
  type Hash,
  type SignableMessage,
  type TypedDataDefinition,
} from 'viem';
import { toAccount } from 'viem/accounts';
import { OneAuthClient } from '../client';
import type { IntentCall, IntentTokenRequest } from '../types';
import { toOneAuthError } from '../errors';
import type {
  PasskeyWalletClientConfig,
  SendCallsParams,
  TransactionCall,
} from './types';
import {
  encodeWebAuthnSignature,
  hashCalls,
  buildTransactionReview,
} from './utils';

export type { PasskeyWalletClientConfig, TransactionCall, SendCallsParams } from './types';
export { encodeWebAuthnSignature, hashCalls } from './utils';

/**
 * Extended WalletClient with passkey signing and batch transaction support
 */
export type PasskeyWalletClient = WalletClient & {
  /**
   * Send multiple calls as a single batched transaction
   * Opens the passkey modal for user approval
   */
  sendCalls: (params: SendCallsParams) => Promise<Hash>;
};

/**
 * Convert viem's message union into the string-only SDK message surface.
 */
function formatSignableMessage(message: SignableMessage): string {
  if (typeof message === 'string') return message;
  return typeof message.raw === 'string' ? message.raw : toHex(message.raw);
}

/**
 * Create a viem-compatible WalletClient that uses passkeys for signing
 *
 * @example
 * ```typescript
 * import { createPasskeyWalletClient } from '@rhinestone/1auth';
 * import { baseSepolia } from 'viem/chains';
 * import { http } from 'viem';
 *
 * const walletClient = createPasskeyWalletClient({
 *   accountAddress: '0x...',
 *   providerUrl: 'https://passkey.1auth.app',
 *   clientId: 'my-dapp',
 *   chain: baseSepolia,
 *   transport: http(),
 * });
 *
 * // Standard viem API
 * const hash = await walletClient.sendTransaction({
 *   to: '0x...',
 *   data: '0x...',
 *   value: 0n,
 * });
 *
 * // Batched transactions
 * const batchHash = await walletClient.sendCalls({
 *   calls: [
 *     { to: '0x...', data: '0x...' },
 *     { to: '0x...', data: '0x...' },
 *   ],
 * });
 * ```
 */
export function createPasskeyWalletClient(
  config: PasskeyWalletClientConfig
): PasskeyWalletClient {
  const identity = {
    accountAddress: config.accountAddress,
  };

  const provider = new OneAuthClient({
    providerUrl: config.providerUrl,
    clientId: config.clientId,
    dialogUrl: config.dialogUrl,
    theme: config.theme,
    blind_signing: config.blind_signing,
    testnets: config.testnets,
    sponsorship: config.sponsorship,
  });

  /**
   * Sign an arbitrary message using the passkey modal.
   *
   * Hashes the message with EIP-191 (`hashMessage`) before passing it to the
   * modal as the WebAuthn challenge so the on-chain verifier receives a
   * deterministic, typed hash rather than raw user data.
   *
   * @param message - A plain string or a `{ raw: Hex | Uint8Array }` object
   *   as accepted by viem's `SignableMessage`.
   * @returns ABI-encoded WebAuthn signature as a `0x`-prefixed hex string.
   */
  const signMessageImpl = async (message: SignableMessage): Promise<`0x${string}`> => {
    const result = await provider.signMessage({
      ...identity,
      message: formatSignableMessage(message),
      description: 'Sign message',
    });

    if (!result.success) {
      throw toOneAuthError(result.error, 'Signing failed', 'SIGNING_FAILED');
    }

    if (!result.signature) {
      throw toOneAuthError(undefined, 'No signature received', 'SIGNING_FAILED');
    }

    return encodeWebAuthnSignature(result.signature);
  };

  /**
   * Sign a single transaction object using the passkey modal.
   *
   * Normalises the transaction into a `TransactionCall` array, computes a
   * deterministic hash over the calls with {@link hashCalls}, then presents
   * the transaction for user approval before encoding the resulting WebAuthn
   * signature.
   *
   * @param transaction - A viem transaction request object containing at least
   *   `to`, and optionally `data` and `value`.
   * @returns ABI-encoded WebAuthn signature as a `0x`-prefixed hex string.
   */
  const signTransactionImpl = async (transaction: any): Promise<`0x${string}`> => {
    const calls: TransactionCall[] = [
      {
        to: transaction.to!,
        data: transaction.data,
        value: transaction.value,
      },
    ];

    const hash = hashCalls(calls);

    const result = await provider.signWithModal({
      challenge: hash,
      ...identity,
      description: 'Sign transaction',
      transaction: buildTransactionReview(calls),
    });

    if (!result.success) {
      throw toOneAuthError(result.error, 'Signing failed', 'SIGNING_FAILED');
    }

    if (!result.signature) {
      throw toOneAuthError(undefined, 'No signature received', 'SIGNING_FAILED');
    }

    return encodeWebAuthnSignature(result.signature);
  };

  /**
   * Sign EIP-712 typed data using the passkey modal.
   *
   * Hashes the structured data with viem's `hashTypedData` (which applies the
   * EIP-712 domain separator and struct hashing) and presents the primary type
   * name to the user as a human-readable label inside the signing dialog.
   *
   * @param typedData - EIP-712 typed data definition including `domain`,
   *   `types`, `primaryType`, and `message`.
   * @returns ABI-encoded WebAuthn signature as a `0x`-prefixed hex string.
   */
  const signTypedDataImpl = async (typedData: any): Promise<`0x${string}`> => {
    const hash = hashTypedData(typedData as TypedDataDefinition);

    const result = await provider.signWithModal({
      challenge: hash,
      ...identity,
      description: 'Sign typed data',
      transaction: {
        actions: [
          {
            type: 'custom',
            label: 'Sign Data',
            sublabel: typedData.primaryType || 'Typed Data',
          },
        ],
      },
    });

    if (!result.success) {
      throw toOneAuthError(result.error, 'Signing failed', 'SIGNING_FAILED');
    }

    if (!result.signature) {
      throw toOneAuthError(undefined, 'No signature received', 'SIGNING_FAILED');
    }

    return encodeWebAuthnSignature(result.signature);
  };

  /**
   * Build the intent payload shared by `sendTransaction` and `sendCalls`.
   *
   * Converts `TransactionCall` objects (viem-style, with bigint values) into
   * `IntentCall` objects (serialisable, with string values) expected by the
   * passkey service. The target chain defaults to `config.chain.id` when no
   * override is provided.
   *
   * @param calls - One or more transaction calls to include in the intent.
   * @param targetChainOverride - Optional chain ID; overrides `config.chain.id`
   *   when the intent should execute on a chain different from the client's
   *   default (e.g. cross-chain intent routing).
   * @param extra - Optional token request and source asset metadata forwarded
   *   to the Rhinestone orchestrator for cross-chain bridging intents.
   * @returns A plain object ready to spread into `provider.sendIntent(...)`.
   */
  const buildIntentPayload = async (
    calls: TransactionCall[],
    targetChainOverride?: number,
    extra?: { tokenRequests?: IntentTokenRequest[]; sourceAssets?: string[] },
  ) => {
    const targetChain = targetChainOverride ?? config.chain.id;
    const intentCalls: IntentCall[] = calls.map((call) => ({
      to: call.to,
      data: call.data || "0x",
      value: call.value !== undefined ? call.value.toString() : "0",
      label: call.label,
      sublabel: call.sublabel,
      icon: call.icon,
      abi: call.abi,
    }));

    return {
      accountAddress: config.accountAddress,
      targetChain,
      calls: intentCalls,
    };
  };

  // toAccount returns a generic LocalAccount<string> but viem's WalletClient
  // constructor expects LocalAccount<"custom"> — the type assertion satisfies
  // this constraint without changing runtime behaviour.
  const account = toAccount({
    address: config.accountAddress,
    signMessage: ({ message }) => signMessageImpl(message),
    signTransaction: signTransactionImpl,
    signTypedData: signTypedDataImpl as any,
  });

  // Create the base wallet client
  const client = createWalletClient({
    account,
    chain: config.chain,
    transport: config.transport,
  });

  // Extend with sendCalls for batched transactions
  const extendedClient = Object.assign(client, {
    /**
     * Send a single transaction via intent flow
     */
    async sendTransaction(transaction: any): Promise<Hash> {
      const targetChain =
        typeof transaction.chainId === "number"
          ? transaction.chainId
          : transaction.chain?.id;
      const calls: TransactionCall[] = [
        {
          to: transaction.to!,
          data: transaction.data || "0x",
          value: transaction.value,
        },
      ];

      const intentPayload = await buildIntentPayload(calls, targetChain);
      const result = await provider.sendIntent({
        ...intentPayload,
        waitForHash: config.waitForHash ?? true,
        hashTimeoutMs: config.hashTimeoutMs,
        hashIntervalMs: config.hashIntervalMs,
      });

      if (!result.success) {
        throw toOneAuthError(result.error, "Transaction failed", "INTENT_FAILED");
      }
      if (!result.transactionHash) {
        throw toOneAuthError(undefined, "No transaction hash received", "HASH_UNAVAILABLE");
      }

      return result.transactionHash as Hash;
    },
    /**
     * Send multiple calls as a single batched transaction
     */
    async sendCalls(params: SendCallsParams): Promise<Hash> {
      const { calls, chainId: targetChain, tokenRequests, sourceAssets, sourceChainId } = params;
      const intentPayload = await buildIntentPayload(calls, targetChain, { tokenRequests, sourceAssets });
      const result = await provider.sendIntent({
        ...intentPayload,
        tokenRequests,
        sourceAssets,
        sourceChainId,
        waitForHash: config.waitForHash ?? true,
        hashTimeoutMs: config.hashTimeoutMs,
        hashIntervalMs: config.hashIntervalMs,
      });

      if (!result.success) {
        throw toOneAuthError(result.error, "Transaction failed", "INTENT_FAILED");
      }
      if (!result.transactionHash) {
        throw toOneAuthError(undefined, "No transaction hash received", "HASH_UNAVAILABLE");
      }

      return result.transactionHash as Hash;
    },
  });

  return extendedClient as PasskeyWalletClient;
}
